import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import {
  Button,
  Divider,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  SearchBox,
  SplitButton,
  Switch,
  Tooltip,
  makeStyles,
  mergeClasses
} from "@fluentui/react-components";
import {
  Checkmark16Filled,
  ChevronDownFilled,
  ChevronUpFilled,
  ClosedCaption24Filled,
  ClosedCaptionOff24Filled,
  Translate24Regular
} from "@fluentui/react-icons";
import { useTranscription } from "../transcription/TranscriptionContext";
import { getLanguageLabel } from "../transcription/transcription-language-utils";

const useClasses = makeStyles({
  splitButtonPressed: {
    "&& button:first-child": {
      backgroundColor: "var(--primaryColorBackground) !important"
    },
    "&& button:last-child": {
      backgroundColor: "var(--primaryColorBackground) !important"
    },
    "&& .fui-SplitButton__primaryActionButton": {
      backgroundColor: "var(--primaryColorBackground) !important"
    },
    "&& .fui-SplitButton__menuButton": {
      backgroundColor: "var(--primaryColorBackground) !important"
    }
  },
  buttonPressed: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  popover: {
    minWidth: "260px",
    maxWidth: "320px",
    padding: "8px"
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: "8px",
    padding: "2px 2px 8px 2px"
  },
  rowLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "var(--colorNeutralForeground1)"
  },
  splitButtonPressedHover: {
    "&& button:first-child": {
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--primaryColorBackground) !important"
      }
    },
    "&& button:last-child": {
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--primaryColorBackground) !important"
      }
    },
    "&& .fui-SplitButton__primaryActionButton": {
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--primaryColorBackground) !important"
      }
    },
    "&& .fui-SplitButton__menuButton": {
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--primaryColorBackground) !important"
      }
    }
  }
});

export function FluentToolbarCCButton({ styles }) {
  const intl = useIntl();
  const classes = useClasses();
  const [targetLanguageSearchQuery, setTargetLanguageSearchQuery] = useState("");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isTargetLanguageMenuOpen, setIsTargetLanguageMenuOpen] = useState(false);

  const {
    hasTranscriptionAgent,
    isCCEnabled,
    toggleCC,
    translationEnabled,
    agentAllowsTranslation,
    targetLanguage,
    setTargetLanguage,
    toggleTranslation,
    availableLanguages
  } = useTranscription();

  const captionIcon = isCCEnabled ? <ClosedCaption24Filled /> : <ClosedCaptionOff24Filled />;

  const tooltipText = isCCEnabled
    ? intl.formatMessage({ id: "toolbar.cc-on", defaultMessage: "Captions on" })
    : intl.formatMessage({ id: "toolbar.cc-off", defaultMessage: "Captions off" });
  const activeCcButtonClassName = styles.buttonActive
    ? mergeClasses(styles.button, styles.buttonActive)
    : mergeClasses(styles.button, classes.buttonPressed);

  const filteredTargetLanguages = useMemo(() => {
    const query = targetLanguageSearchQuery.trim().toLowerCase();
    if (!query) return availableLanguages;

    return availableLanguages.filter(languageCode => {
      const label = getLanguageLabel(languageCode).toLowerCase();
      return label.includes(query) || languageCode.toLowerCase().includes(query);
    });
  }, [availableLanguages, targetLanguageSearchQuery]);

  if (!hasTranscriptionAgent) return null;

  if (!agentAllowsTranslation) {
    return (
      <Tooltip content={tooltipText}>
        <Button
          appearance="subtle"
          onClick={toggleCC}
          className={isCCEnabled ? activeCcButtonClassName : styles.button}
          icon={
            isCCEnabled ? (
              <ClosedCaption24Filled style={{ width: "24px", height: "24px" }} />
            ) : (
              <ClosedCaptionOff24Filled style={{ width: "24px", height: "24px" }} />
            )
          }
          title={tooltipText}
        />
      </Tooltip>
    );
  }

  return (
    <Popover
      positioning="above"
      open={isPopoverOpen}
      onOpenChange={(_event, data) => {
        setIsPopoverOpen(data.open);
        if (!data.open) {
          setTargetLanguageSearchQuery("");
          setIsTargetLanguageMenuOpen(false);
        }
      }}
    >
      <PopoverTrigger disableButtonEnhancement>
        {triggerProps => (
          <Tooltip content={tooltipText}>
            <SplitButton
              menuButton={triggerProps}
              appearance="subtle"
              primaryActionButton={{ onClick: toggleCC }}
              className={
                isCCEnabled
                  ? mergeClasses(styles.buttonSplit, classes.splitButtonPressed, classes.splitButtonPressedHover)
                  : styles.buttonSplit
              }
              icon={captionIcon}
              menuIcon={
                <ChevronUpFilled style={{ width: "16px", height: "16px", marginBottom: "4px", color: "var(--color-text-primary)" }} />
              }
              title={tooltipText}
            />
          </Tooltip>
        )}
      </PopoverTrigger>

      <PopoverSurface className={classes.popover}>
        <div className={classes.row}>
          <span className={classes.rowLabel}>
            <Translate24Regular />
            {intl.formatMessage({ id: "toolbar.cc-translate", defaultMessage: "Translate captions" })}
          </span>
          <Switch
            checked={translationEnabled}
            onChange={toggleTranslation}
            title={intl.formatMessage({ id: "toolbar.cc-translate", defaultMessage: "Translate captions" })}
          />
        </div>

        <Menu
          open={isTargetLanguageMenuOpen}
          onOpenChange={(_event, data) => {
            setIsTargetLanguageMenuOpen(data.open);
            if (!data.open) {
              setTargetLanguageSearchQuery("");
            }
          }}
          positioning="above-start"
        >
          <MenuTrigger disableButtonEnhancement>
            <MenuButton
              icon={<Translate24Regular />}
              menuIcon={
                <ChevronDownFilled
                  style={{ width: "18px", height: "18px", display: "block", transform: "translateY(-1px)" }}
                />
              }
              appearance="filled-darker"
              disabled={!translationEnabled}
              style={{
                width: "100%",
                justifyContent: "space-between",
                textAlign: "left",
                alignItems: "center",
                background: "var(--color-brand-subtle)",
                borderColor: "transparent"
              }}
            >
              <span style={{ flex: 1, textAlign: "left", display: "inline-flex", alignItems: "center" }}>
                {getLanguageLabel(targetLanguage)}
              </span>
            </MenuButton>
          </MenuTrigger>

          <MenuPopover>
            <div style={{ padding: "4px" }}>
              <SearchBox
                placeholder={intl.formatMessage({
                  id: "toolbar.cc-target-language-search",
                  defaultMessage: "Search target language..."
                })}
                value={targetLanguageSearchQuery}
                onChange={(_event, data) => setTargetLanguageSearchQuery(data.value)}
                appearance="filled-darker"
              />
            </div>

            <Divider style={{ margin: "8px 0" }} />

            <MenuList>
              {filteredTargetLanguages.map(languageCode => {
                const isSelected = targetLanguage === languageCode;

                return (
                  <MenuItem
                    key={`target-${languageCode}`}
                    icon={isSelected ? <Checkmark16Filled /> : undefined}
                    onClick={() => {
                      setTargetLanguage(languageCode);
                      setIsTargetLanguageMenuOpen(false);
                      setTargetLanguageSearchQuery("");
                    }}
                  >
                    {getLanguageLabel(languageCode)}
                  </MenuItem>
                );
              })}

              {filteredTargetLanguages.length === 0 && (
                <MenuItem disabled>
                  {intl.formatMessage({
                    id: "toolbar.cc-no-target-language",
                    defaultMessage: "No language matches"
                  })}
                </MenuItem>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      </PopoverSurface>
    </Popover>
  );
}

FluentToolbarCCButton.propTypes = {
  styles: PropTypes.object
};
